import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { Button } from '../ui/components/Button';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { formatAddress } from '../utils/formatters';
import { getDiscordLinkUrl } from '../api/settings';
import {
  getAmbassadorStatus,
  applyAmbassadorProgram,
  leaveAmbassadorProgram,
  getLeaderboard,
  getMyAllocations,
  getMyActiveAllocations,
  submitXPost,
  type AmbassadorStatus,
  type LeaderboardResponse,
  type AmbassadorAllocation,
} from '../api/ambassadors';

export function AmbassadorsPage() {
  const { isAuthenticated, user } = useAuthStore();
  const { success: showSuccess, error: showError } = useNotificationStore();

  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<AmbassadorStatus | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardResponse | null>(null);
  const [allocations, setAllocations] = useState<AmbassadorAllocation[]>([]);
  const [activeAllocations, setActiveAllocations] = useState<AmbassadorAllocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [leaderboardPage, setLeaderboardPage] = useState(1);
  const [xPostUrl, setXPostUrl] = useState('');
  const [submittingPostId, setSubmittingPostId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [lbRes] = await Promise.all([
        getLeaderboard(leaderboardPage),
      ]);
      setLeaderboard(lbRes.data);

      if (isAuthenticated) {
        const statusRes = await getAmbassadorStatus();
        setStatus(statusRes.data);

        if (statusRes.data.enrolled) {
          const [allocRes, activeRes] = await Promise.all([
            getMyAllocations(),
            getMyActiveAllocations(),
          ]);
          setAllocations(allocRes.data.allocations);
          setActiveAllocations(activeRes.data);
        }
      }
    } catch (err) {
      console.error('Failed to load ambassador data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, leaderboardPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle Discord OAuth callback query params
  useEffect(() => {
    const discordResult = searchParams.get('discord');
    if (!discordResult) return;

    if (discordResult === 'linked') {
      showSuccess('Discord verified! Your server membership has been confirmed.');
      fetchData(); // Refresh eligibility
    } else if (discordResult === 'error') {
      const reason = searchParams.get('reason');
      const messages: Record<string, string> = {
        not_in_server: 'You must join the xrp.deals Discord server first, then click Re-verify.',
        exchange_failed: 'Failed to connect to Discord. Please try again.',
        invalid_state: 'Your session expired. Please try again.',
      };
      showError(messages[reason || ''] || 'Something went wrong. Please try again.');
    }

    searchParams.delete('discord');
    searchParams.delete('reason');
    setSearchParams(searchParams, { replace: true });
  }, []);

  const handleApply = async () => {
    try {
      setIsJoining(true);
      await applyAmbassadorProgram();
      showSuccess('Application submitted! We\'ll review it shortly.');
      await fetchData();
    } catch (err: any) {
      showError(err.message || 'Failed to submit application');
    } finally {
      setIsJoining(false);
    }
  };

  const handleLeave = async () => {
    if (!window.confirm('Are you sure you want to leave the Ambassadors Program? Active allocations will be forfeited.')) return;
    try {
      setIsLeaving(true);
      await leaveAmbassadorProgram();
      showSuccess('You have left the Ambassadors Program');
      await fetchData();
    } catch (err: any) {
      showError(err.message || 'Failed to leave program');
    } finally {
      setIsLeaving(false);
    }
  };

  const handleSubmitXPost = async (allocationId: string) => {
    if (!xPostUrl.trim()) {
      showError('Please enter your X post URL');
      return;
    }
    try {
      setSubmittingPostId(allocationId);
      await submitXPost(allocationId, xPostUrl.trim());
      showSuccess('X post submitted successfully!');
      setXPostUrl('');
      await fetchData();
    } catch (err: any) {
      showError(err.message || 'Failed to submit X post');
    } finally {
      setSubmittingPostId(null);
    }
  };

  if (isLoading) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full flex-col items-start">
          <div className="container max-w-none flex w-full grow shrink-0 basis-0 flex-col items-center gap-4 bg-default-background py-12">
            <div className="text-center py-12 text-subtext-color">Loading...</div>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  const enrolled = status?.enrolled === true;
  const ambassador = status?.ambassador;
  const eligibility = status?.eligibility;

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start">
        <div className="container max-w-none flex w-full grow shrink-0 basis-0 flex-col items-center gap-8 bg-default-background py-12 px-4">
          <div className="w-full max-w-[960px] flex flex-col gap-8">

            {/* Hero Section */}
            <div className="text-center">
              <h1 className="text-heading-1 font-heading-1 text-default-font mb-2">
                Ambassadors Program
              </h1>
              <p className="text-body font-body text-subtext-color">
                Get rewarded for supporting the community. Receive free NFTs from new launches, earn recognition on the leaderboard, and help promote amazing collections.
              </p>
            </div>

            {/* Enrolled: Score & Rank Card */}
            {enrolled && ambassador && (
              <div className="bg-neutral-50 border border-neutral-border rounded-lg p-6">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-default-font">{Number(ambassador.score).toFixed(1)}</div>
                      <div className="text-sm text-subtext-color">Score</div>
                    </div>
                    <div className="h-10 w-px bg-neutral-border" />
                    <div className="text-center">
                      <div className="text-3xl font-bold text-default-font">{(status?.totalActive ?? 0) >= 10 ? `#${status?.rank ?? '—'}` : 'N/A'}</div>
                      <div className="text-sm text-subtext-color">Rank</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-brand-100 text-brand-700 text-sm font-medium">
                      Ambassador
                    </span>
                    <Button
                      variant="neutral-secondary"
                      size="small"
                      onClick={handleLeave}
                      loading={isLeaving}
                    >
                      Leave Program
                    </Button>
                  </div>
                </div>
                {Number(ambassador.deals_balance) > 0 && (
                  <div className="mt-3 pt-3 border-t border-neutral-border flex items-center gap-4 text-sm text-subtext-color">
                    <span><a href="https://firstledger.net/token-v2/rfnKKs998kvuVvJSW4jZ1MDxMSiCjhs9su/4445414C53000000000000000000000000000000" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">$DEALS</a> Holdings: {Math.round(Number(ambassador.deals_balance)).toLocaleString()}</span>
                  </div>
                )}
                <p className="text-sm text-subtext-color mt-3">
                  Your score is based on a combination of factors including minting from launches on xrp.deals, <a href="https://firstledger.net/token-v2/rfnKKs998kvuVvJSW4jZ1MDxMSiCjhs9su/4445414C53000000000000000000000000000000" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">$DEALS</a> token holdings, how you engage with collections you receive through the program, posting about launches on X, creator feedback, and engagement with <a href="https://x.com/XRPDeals" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">@XRPDeals</a> on X and in our Discord server. Active and consistent participation is the best way to increase your score over time.
                </p>
                <p className="text-sm text-subtext-color mt-2">
                  Higher-ranked ambassadors are more likely to be selected for free NFT allocations from new launches. Stay active and keep your score growing to improve your chances.
                </p>
              </div>
            )}

            {/* Application pending */}
            {!enrolled && isAuthenticated && status?.applicationStatus === 'pending' && (
              <div className="bg-neutral-50 border border-neutral-border rounded-lg p-6">
                <h2 className="text-heading-3 font-heading-3 text-default-font mb-3">Application Submitted</h2>
                <p className="text-body text-subtext-color">
                  Your application is under review. We'll notify you by email once a decision has been made.
                </p>
              </div>
            )}

            {/* Application rejected — allow re-apply */}
            {!enrolled && isAuthenticated && status?.applicationStatus === 'rejected' && eligibility && (
              <div className="bg-neutral-50 border border-neutral-border rounded-lg p-6">
                <h2 className="text-heading-3 font-heading-3 text-default-font mb-3">Application Not Approved</h2>
                <p className="text-body text-subtext-color mb-4">
                  Your previous application was not approved. You're welcome to re-apply — continuing to be active on the platform will strengthen your application.
                </p>
                <Button
                  onClick={handleApply}
                  loading={isJoining}
                  disabled={!eligibility.eligible}
                >
                  Re-Apply to the Ambassadors Program
                </Button>
              </div>
            )}

            {/* Not enrolled, no application: Requirements & Apply */}
            {!enrolled && isAuthenticated && !status?.applicationStatus && eligibility && (() => {
              const required = eligibility.requirements.filter(r => !r.orGroup);
              const orGroupReqs = eligibility.requirements.filter(r => r.orGroup);
              const orGroupMet = orGroupReqs.some(r => r.met);
              return (
                <div className="bg-neutral-50 border border-neutral-border rounded-lg p-6">
                  <h2 className="text-heading-3 font-heading-3 text-default-font mb-4">Requirements</h2>

                  {/* Always-required items */}
                  <div className="flex flex-col gap-3 mb-4">
                    {required.map((req) => (
                      <div key={req.key} className="flex items-center gap-3">
                        <span className={`text-lg ${req.met ? 'text-green-600' : 'text-red-500'}`}>
                          {req.met ? '\u2713' : '\u2717'}
                        </span>
                        <div>
                          <span className="text-body text-default-font">{req.label}</span>
                          {req.current && !req.met && (
                            <span className="text-sm text-subtext-color ml-2">
                              ({req.current})
                            </span>
                          )}
                          {req.actionUrl && !req.met && (
                            req.actionUrl.startsWith('http') ? (
                              <>
                                <a href={req.actionUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-brand-600 hover:underline ml-2">
                                  {req.actionLabel || 'Fix this'}
                                </a>
                                {req.key === 'discord_linked' && (
                                  <a href={getDiscordLinkUrl('/ambassadors')} className="text-sm text-brand-600 hover:underline ml-2">
                                    Re-verify
                                  </a>
                                )}
                              </>
                            ) : (
                              <Link to={req.actionUrl} className="text-sm text-brand-600 hover:underline ml-2">
                                {req.actionLabel || 'Fix this'}
                              </Link>
                            )
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* OR group */}
                  {orGroupReqs.length > 0 && (
                    <>
                      <p className="text-sm font-medium text-subtext-color mb-3">Plus at least one of the following:</p>
                      <div className="flex flex-col gap-3 mb-6">
                        {orGroupReqs.map((req) => (
                          <div key={req.key} className="flex items-center gap-3">
                            <span className={`text-lg ${req.met ? 'text-green-600' : orGroupMet ? 'text-subtext-color' : 'text-red-500'}`}>
                              {req.met ? '\u2713' : '\u2717'}
                            </span>
                            <div>
                              <span className="text-body text-default-font">{req.label}</span>
                              {req.current && !req.met && (
                                <span className="text-sm text-subtext-color ml-2">
                                  (Your volume: {req.current})
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {(!eligibility.requirements.find(r => r.key === 'email_confirmed')?.met ||
                    !eligibility.requirements.find(r => r.key === 'x_account')?.met ||
                    (!eligibility.requirements.find(r => r.key === 'discord_linked')?.met &&
                     !eligibility.requirements.find(r => r.key === 'discord_linked')?.current)) && (
                    <p className="text-sm text-subtext-color mb-4">
                      <Link to="/settings" className="text-brand-600 hover:underline">Update your Settings</Link> to verify your email, link your X account, and connect your Discord.
                    </p>
                  )}
                  <div className="flex justify-center">
                    <Button
                      onClick={handleApply}
                      loading={isJoining}
                      disabled={!eligibility.eligible}
                    >
                      {eligibility.eligible ? 'Apply to the Ambassadors Program' : 'Complete all requirements to apply'}
                    </Button>
                  </div>
                  {ambassador?.status === 'banned' && (
                    <p className="text-sm text-red-600 mt-2">You have been banned from this program.</p>
                  )}
                </div>
              );
            })()}

            {/* Not logged in */}
            {!isAuthenticated && (
              <div className="bg-neutral-50 border border-neutral-border rounded-lg p-6 text-center">
                <p className="text-body text-default-font mb-4">Connect your wallet to check eligibility and join the Ambassadors Program.</p>
              </div>
            )}

            {/* Active Selections */}
            {enrolled && activeAllocations.length > 0 && (
              <div>
                <h2 className="text-heading-3 font-heading-3 text-default-font mb-4">Active Selections</h2>
                <div className="flex flex-col gap-3">
                  {activeAllocations.map((alloc) => (
                    <div key={alloc.id} className="bg-brand-50 border border-brand-200 rounded-lg p-4 flex items-center justify-between">
                      <div>
                        <span className="font-medium text-default-font">{alloc.launch_name}</span>
                        <p className="text-sm text-subtext-color">
                          {alloc.status === 'selected' ? 'Selected! Claim your free mint on the drop page.' : ''}
                        </p>
                      </div>
                      <Link
                        to={`/drops/${alloc.launch_slug}`}
                        className="text-brand-600 hover:underline text-sm font-medium"
                      >
                        Go to Drop
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* My Ambassador Mints */}
            {enrolled && allocations.length > 0 && (
              <div>
                <h2 className="text-heading-3 font-heading-3 text-default-font mb-4">My Ambassador Mints</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-neutral-border">
                        <th className="py-2 px-3 text-sm font-medium text-subtext-color">Collection</th>
                        <th className="py-2 px-3 text-sm font-medium text-subtext-color">Claimed</th>
                        <th className="py-2 px-3 text-sm font-medium text-subtext-color">Hold Status</th>
                        <th className="py-2 px-3 text-sm font-medium text-subtext-color">X Post</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allocations.map((alloc) => {
                        const isLocked = alloc.lock_expires_at && new Date(alloc.lock_expires_at) > new Date();
                        const daysRemaining = alloc.lock_expires_at
                          ? Math.max(0, Math.ceil((new Date(alloc.lock_expires_at).getTime() - Date.now()) / 86400000))
                          : 0;

                        return (
                          <tr key={alloc.id} className="border-b border-neutral-border">
                            <td className="py-3 px-3">
                              <Link to={`/drops/${alloc.launch_slug}`} className="text-brand-600 hover:underline">
                                {alloc.launch_name}
                              </Link>
                            </td>
                            <td className="py-3 px-3 text-sm text-subtext-color">
                              {alloc.claimed_at ? new Date(alloc.claimed_at).toLocaleDateString() : alloc.status === 'expired' ? 'Expired' : 'Pending'}
                            </td>
                            <td className="py-3 px-3 text-sm">
                              {alloc.violation_detected_at ? (
                                <span className="text-red-600">Violation detected</span>
                              ) : alloc.status !== 'claimed' ? (
                                <span className="text-subtext-color">—</span>
                              ) : isLocked ? (
                                <span className="text-amber-600">Locked ({daysRemaining} days remaining)</span>
                              ) : (
                                <span className="text-green-600">Unlocked</span>
                              )}
                            </td>
                            <td className="py-3 px-3 text-sm">
                              {alloc.x_post_url ? (
                                <a href={alloc.x_post_url} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
                                  View Post
                                </a>
                              ) : alloc.status === 'claimed' && !alloc.x_post_submitted_at ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    placeholder="https://x.com/..."
                                    value={submittingPostId === alloc.id ? xPostUrl : ''}
                                    onChange={(e) => { setSubmittingPostId(alloc.id); setXPostUrl(e.target.value); }}
                                    className="border border-neutral-border rounded px-2 py-1 text-sm w-48"
                                  />
                                  <Button
                                    size="small"
                                    onClick={() => handleSubmitXPost(alloc.id)}
                                    loading={submittingPostId === alloc.id}
                                    disabled={submittingPostId === alloc.id && !xPostUrl.trim()}
                                  >
                                    Submit
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-subtext-color">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Ambassador Rankings — only visible to enrolled ambassadors, hidden until 10+ members (always visible for admin) */}
            {enrolled && ((leaderboard?.total ?? 0) >= 10 || user?.xrpl_address === 'rNvbPBdxJRwS5nU1dGBHDBtaAh4B9vvRoB') && <div>
              <h2 className="text-heading-3 font-heading-3 text-default-font mb-4">Ambassador Rankings</h2>

              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-neutral-border">
                      <th className="py-2 px-3 text-sm font-medium text-subtext-color w-16">Rank</th>
                      <th className="py-2 px-3 text-sm font-medium text-subtext-color">Ambassador</th>
                      <th className="py-2 px-3 text-sm font-medium text-subtext-color text-right">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard?.ambassadors.map((amb, idx) => {
                      const rank = (leaderboardPage - 1) * 50 + idx + 1;
                      const isCurrentUser = user?.xrpl_address === amb.wallet_address;
                      return (
                        <tr
                          key={amb.id}
                          className={`border-b border-neutral-border ${isCurrentUser ? 'bg-brand-50' : ''}`}
                        >
                          <td className="py-3 px-3 text-sm font-medium text-default-font">#{rank}</td>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2">
                              {amb.avatar_url && (
                                <img src={amb.avatar_url} alt="" className="w-6 h-6 rounded-full" />
                              )}
                              <span className="text-sm text-default-font">
                                {amb.username || formatAddress(amb.wallet_address)}
                              </span>
                              {isCurrentUser && (
                                <span className="text-xs text-brand-600 font-medium">(You)</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-3 text-sm text-default-font text-right font-medium">
                            {Number(amb.score).toFixed(1)}
                          </td>
                        </tr>
                      );
                    })}
                    {(!leaderboard?.ambassadors || leaderboard.ambassadors.length === 0) && (
                      <tr>
                        <td colSpan={3} className="py-8 text-center text-subtext-color">
                          No ambassadors yet. Be the first to join!
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {leaderboard && leaderboard.totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-4">
                  <Button
                    variant="neutral-secondary"
                    size="small"
                    disabled={leaderboardPage <= 1}
                    onClick={() => setLeaderboardPage(p => p - 1)}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-subtext-color">
                    Page {leaderboard.page} of {leaderboard.totalPages}
                  </span>
                  <Button
                    variant="neutral-secondary"
                    size="small"
                    disabled={leaderboardPage >= leaderboard.totalPages}
                    onClick={() => setLeaderboardPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>}

          </div>
        </div>
      </div>
    </DefaultPageLayout>
  );
}
